// Trustline status axis — the lifecycle of a row in
// `investor_crypto_wallet_trustlines`, i.e. whether the investor's wallet has
// agreed on-chain to hold one property's token.
//
// The third status axis in this module, and the only one an order in
// `awaiting_external_opt_in` is blocked on. Distinct from CRYPTO_WALLET_STATUS
// (is the wallet itself validated?) and VASP_USER_STATUS (has Ctrl-Alt accepted
// the investor's organisation?) — see constants/cryptoWalletStatus.ts.
//
// Values mirror admin-service's TRUSTLINE_STATUS enum exactly.
export const TRUSTLINE_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  FAILED: 'failed',
  REMOVED: 'removed',
} as const

export type TrustlineStatus = (typeof TRUSTLINE_STATUS)[keyof typeof TRUSTLINE_STATUS]

type TrustlineStatusColor = 'success' | 'warning' | 'danger' | 'neutral'

// OCP lookup keyed by the raw string, so a value the UI does not know resolves
// to neutral rather than rendering an empty badge.
const TRUSTLINE_STATUS_COLOR_MAP: Record<string, TrustlineStatusColor> = {
  [TRUSTLINE_STATUS.ACTIVE]: 'success',
  [TRUSTLINE_STATUS.PENDING]: 'warning',
  [TRUSTLINE_STATUS.FAILED]: 'danger',
  [TRUSTLINE_STATUS.REMOVED]: 'neutral',
}

const TRUSTLINE_STATUS_LABEL_MAP: Record<string, string> = {
  [TRUSTLINE_STATUS.ACTIVE]: 'Active',
  [TRUSTLINE_STATUS.PENDING]: 'Awaiting ledger',
  [TRUSTLINE_STATUS.FAILED]: 'Failed',
  [TRUSTLINE_STATUS.REMOVED]: 'Removed',
}

export function trustlineStatusColor(status: string | null | undefined): TrustlineStatusColor {
  if (!status) return 'neutral'
  return TRUSTLINE_STATUS_COLOR_MAP[status] ?? 'neutral'
}

// "Not set up" rather than an empty cell: a missing trustline is the state the
// operator has to act on, so it needs a label of its own.
export function trustlineStatusLabel(status: string | null | undefined): string {
  if (!status) return 'Not set up'
  return TRUSTLINE_STATUS_LABEL_MAP[status] ?? status
}
